import { Icon, SvgIconNames } from '@toggle/design-system';
import { formatPercentage, formatPriceWithCurrency } from '@toggle/helpers';
import { EarningsInsight } from '@toggle/toggle';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { TooltipContainer } from '~/components/tooltip-container/TooltipContainer';
import {
  SubTabsType,
  useEarningsInsightDrawer,
} from '~/views/earnings-insight-drawer/useEarningsInsightDrawer';

import * as S from './EarningsInsightIcon.styles';

export interface EarningsInsightIconProps {
  size?: number;
  entityTag: string;
  earningsInsight: EarningsInsight;
}

export const EarningsInsightIcon = ({
  size,
  entityTag,
  earningsInsight,
}: EarningsInsightIconProps) => {
  const { t } = useTranslation(['earnings']);
  const { openDrawer } = useEarningsInsightDrawer();
  // TODO: once BE adds the new endpoints for earnings data that take in an earningsId, we need to integrate to get EPS and delta
  const deltaValue = 0.1;
  const epsValue = 0.21;

  const isPreview = earningsInsight.type === 'preview';
  const iconProps = {
    iconName: (isPreview ? 'EarningsPreview' : 'EarningsRecap') as SvgIconNames,
    viewBox: '0 0 30 32',
  };
  const title = isPreview
    ? t('earnings:earningsInsight.drawer.earningsPreview')
    : t('earnings:earningsInsight.drawer.earningsRecap');
  const epsLabel = isPreview
    ? t('earnings:earningsInsight.drawer.epsExpectations')
    : t('earnings:earningsInsight.drawer.epsActual');
  const isNegativeDelta = !!deltaValue && deltaValue < 0;

  return (
    <TooltipContainer
      label={
        <S.TooltipWrapper>
          <Icon {...iconProps} size={32} />
          <S.RightWrapper>
            <S.TitleLabel>{title}</S.TitleLabel>
            <S.BottomWrapper>
              <S.DescriptionLabel>{epsLabel}</S.DescriptionLabel>
              <S.ValueWrapper>
                {formatPriceWithCurrency(epsValue)}
              </S.ValueWrapper>
              {!isPreview && !!deltaValue && (
                <S.RecapWrapper>
                  <S.RecapValue $isNegative={isNegativeDelta}>
                    {formatPercentage(deltaValue, {
                      suffix: '%',
                      addPlus: !isNegativeDelta,
                    })}
                  </S.RecapValue>
                  <S.RecapLabel>
                    {t('earnings:earningsInsight.drawer.vsConsExp')}
                  </S.RecapLabel>
                </S.RecapWrapper>
              )}
            </S.BottomWrapper>
          </S.RightWrapper>
        </S.TooltipWrapper>
      }
      placement="top-start"
      offsetY={-5}
      offsetX={8}
      hasMaxWidth={false}
    >
      <S.StyledIcon
        {...iconProps}
        size={size}
        onClick={e => {
          e.stopPropagation();
          openDrawer(
            entityTag,
            earningsInsight.earnings_id,
            isPreview ? SubTabsType.Preview : SubTabsType.Recap
          );
        }}
      />
    </TooltipContainer>
  );
};
